import React, { Component, Fragment } from 'react';

import './DataTable.scss';
import Icon from 'Core/Components/Icon';
import LoadingSpinner from 'Core/Components/LoadingSpinner';
import { equals, createSorter } from 'Core/Utils/ObjectUtils';
import { isString, isFunction, isNonEmptyArray, isArray, isNumber } from 'Core/Utils/TypeUtils';

class DataTable extends Component {
  constructor (props) {
    super(props);

    const defaultSortIndex = this.getDefaultSort()
    const defaultSortAscending = true;

    this.state = {
      sortColumnIndex: defaultSortIndex,
      sortAscending: defaultSortAscending,
      expandedRowIndexes: []
    };

    this.getRowClass = this.getRowClass.bind(this);
    this.flipSortOrder = this.flipSortOrder.bind(this);
    this.getDefaultSort = this.getDefaultSort.bind(this);
    this.isTableSortable = this.isTableSortable.bind(this);
    this.handleCellClick = this.handleCellClick.bind(this);
    this.toggleExpandRow = this.toggleExpandRow.bind(this);
    this.setSortColumnIndex = this.setSortColumnIndex.bind(this);
    this.getColumnHeaderClass = this.getColumnHeaderClass.bind(this);
    this.handleColumnHeaderClick = this.handleColumnHeaderClick.bind(this);
  }

  setSortColumnIndex (sortColumnIndex) {
    this.setState({ sortColumnIndex });
  }

  handleRowClick (row, rowIndex) {
    const { onRowClick, expandRow } = this.props;
    if (isFunction(onRowClick)) {
      onRowClick(row, rowIndex);
    } else if (isFunction(expandRow)) {
      this.toggleExpandRow(rowIndex);
    }
  }

  toggleExpandRow (rowIndex) {
    const { expandedRowIndexes: currentlyExpanded } = this.state;

    const isExpanded = currentlyExpanded.includes(rowIndex);
    const expandedRowIndexes = isExpanded
      ? currentlyExpanded.filter(i => i !== rowIndex)
      : currentlyExpanded.concat(rowIndex);

    this.setState({ expandedRowIndexes });
  }

  handleCellClick (column, columnIndex, row, rowIndex) {
    if (isFunction(column.onClick))
      column.onClick(row, rowIndex);
    else
      this.handleRowClick(row, rowIndex);
  }

  getSortedRows (rows, sortColumnIndex, sortAscending) {
    const { columns } = this.props;

    if (!isNumber(sortColumnIndex) || !this.isTableSortable())
      return rows;

    const sortColumn = columns[sortColumnIndex];
    const sortedRows = rows.sort(createSorter(sortColumn.getSortValue));

    return sortAscending ? sortedRows : sortedRows.reverse();
  }

  isTableSortable () {
    const { columns } = this.props;

    return isArray(columns)
      && columns.length
      && columns.some(({ getSortValue }) => isFunction(getSortValue));
  }

  getDefaultSort () {
    const { columns } = this.props;

    return !this.isTableSortable()
      ? null
      : columns.reduce((result, { defaultSort }, index) => defaultSort ? index : result, null);
  }

  getColumnHeaderClass (column, columnIndex) {
    const { sortColumnIndex } = this.state;

    let columnHeaderClass = 'data-table-column-header';

    if (isFunction(column.getSortValue))
      columnHeaderClass += ' column-sortable';
    if (sortColumnIndex === columnIndex)
      columnHeaderClass += ' column-sorted';
    if (isString(column.className))
      columnHeaderClass += ' ' + column.className;

    return columnHeaderClass;
  }

  handleColumnHeaderClick (column, columnIndex) {
    const { sortColumnIndex } = this.state;

    if (!isFunction(column.getSortValue))
      return;
    if (columnIndex === sortColumnIndex) {
      this.flipSortOrder();
      this.applyRowSort();
    } else {
      this.setSortColumnIndex(columnIndex);
    }
  }

  flipSortOrder () {
    const { sortAscending } = this.state;

    this.setState({ sortAscending: !sortAscending });
  }

  getRowClass (row, rowIndex) {
    const { rowClass, expandRow, onRowClick } = this.props;

    var baseClass = 'data-table-row '
      + (isFunction(expandRow) ? 'row-expandable ' : '')
      + (isFunction(onRowClick) ? 'row-clickable ' : '');

    if (isString(rowClass)) {
      return baseClass + rowClass;
    } else if (isFunction(rowClass)) {
      return baseClass + rowClass(row);
    }

    return baseClass;
  }

  render () {
    const { rows, columns, loading, className, expandRow, children, hideHeadings } = this.props;
    const { sortColumnIndex, sortAscending, expandedRowIndexes } = this.state;

    const sortedRows = this.getSortedRows(rows, sortColumnIndex, sortAscending);
    const HeaderIconAndName = ({ column }) => (
      <React.Fragment>
        {column.icon && sortColumnIndex !== columnIndex && (
          <Icon fa={column.icon} className="column-icon"/>
        )}
        <nobr>{column.name}</nobr>
      </React.Fragment>
    );

    return !isNonEmptyArray(columns) ? null : (
      <table className={'data-table ' + (className || '')} cellPadding="0" cellSpacing="0" border="0">
        <tbody>
          <tr>
            {hideHeadings ? null : columns.map((column, columnIndex) => (
              <th
                key={columnIndex}
                onClick={() => this.handleColumnHeaderClick(column, columnIndex)}
                className={this.getColumnHeaderClass(column, columnIndex)}>

                {sortColumnIndex === columnIndex && (
                  <Icon fa={'sort-amount-' + (sortAscending ? 'asc' : 'desc')} className="column-icon text-calm"/>
                )}
                <HeaderIconAndName column={column}/>
              </th>
            ))}
          </tr>

          {loading && (
            <tr>
              <td colSpan={columns.length} className="cell-loading">
                <div className="empty-table-content">
                  <LoadingSpinner />
                </div>
              </td>
            </tr>
          )}

          {!loading && (!sortedRows || !sortedRows.length) && (
            <tr>
              <td colSpan={columns.length} className="text-center">
                  {children ? children : (
                    <div className="empty-table-content">
                      <span className="text-muted">No results found.</span>
                    </div>
                  )}
              </td>
            </tr>
          )}

          {!loading && sortedRows && sortedRows.map((row, rowIndex) => (
            <Fragment key={rowIndex}>
              <tr className={this.getRowClass(row, rowIndex)}>
                {columns.map((column, columnIndex) => (
                  <td
                    key={columnIndex}
                    className={'data-table-cell ' + (column.className || '')}
                    style={column.columnStyle || {}}
                    onClick={() => this.handleCellClick(column, columnIndex, row, rowIndex)}>
                    <div className="data-table-header-adjacent">
                      <HeaderIconAndName column={column}/>
                    </div>
                    {column.renderCell(row, rowIndex)}
                  </td>
                ))}
              </tr>
              {expandRow && expandedRowIndexes.includes(rowIndex) && (
                <tr className="data-table-row-expanded">
                  <td colSpan={columns.length}>
                    {expandRow(row, rowIndex)}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    );
  }
};

export default DataTable;



// WEBPACK FOOTER //
// ./client/Core/Components/DataTable/DataTable.jsx